import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'designer' | 'client';
  invitationAccepted?: boolean;
  invitationSentAt?: string;
  subscriptionTier?: 'unlimited' | 'two_projects' | 'one_project';
}

const TIER_LABELS: Record<string, string> = {
  unlimited: 'Unlimited',
  two_projects: 'Two Projects',
  one_project: 'One Project',
}

const UserManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'designer' as 'admin' | 'designer' | 'client',
    subscriptionTier: 'unlimited' as 'unlimited' | 'two_projects' | 'one_project',
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      alert('Failed to load users. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      alert('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'designer',
      subscriptionTier: 'unlimited',
    });
    setShowModal(true);
  };

  const handleEditUser = (userToEdit: User) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      role: userToEdit.role,
      subscriptionTier: userToEdit.subscriptionTier || 'unlimited',
    });
    setShowModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Delete this user? This action cannot be undone.')) return;

    try {
      await api.delete(`/users/${userId}`);
      setUsers(prev => prev.filter(u => u._id !== userId));
      alert('User deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      await api.post('/auth/resend-invitation', { userId });
      alert('Invitation resent successfully');
      fetchUsers(); // Refresh to get updated invitationSentAt
    } catch (error: any) {
      console.error('Failed to resend invitation:', error);
      alert('Failed to resend invitation: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      if (editingUser) {
        // Update existing user (no password change in invitation system)
        await api.put(`/users/${editingUser._id}`, {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          subscriptionTier: formData.role === 'designer' ? formData.subscriptionTier : undefined,
        });
        alert('User updated successfully');
      } else {
        // Create new user via invitation system
        await api.post('/auth/register-client', {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          subscriptionTier: formData.role === 'designer' ? formData.subscriptionTier : undefined,
        });
        alert('User created and invitation sent successfully');
      }

      setShowModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      alert('Failed to save user: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  if (user?.role !== 'admin') {
    return null; // Don't render anything for non-admin users
  }

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">User Management</h1>
          <div className="space-x-3">
            <button
              onClick={() => navigate('/')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={handleAddUser}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Add User
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md">
          {loading ? (
            <div className="p-8 text-center">
              <div className="text-lg">Loading users...</div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((userItem) => (
                    <tr key={userItem._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{userItem.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-900">{userItem.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userItem.role === 'admin' ? 'bg-red-100 text-red-800' :
                          userItem.role === 'designer' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {userItem.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {userItem.role === 'designer'
                          ? TIER_LABELS[userItem.subscriptionTier || 'unlimited']
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {userItem.invitationAccepted === false ? (
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                            <button
                              onClick={() => handleResendInvitation(userItem._id)}
                              className="text-xs text-blue-600 hover:text-blue-900"
                            >
                              Resend
                            </button>
                          </div>
                        ) : userItem.invitationAccepted === true ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleEditUser(userItem)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(userItem._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal for Add/Edit User */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email"
                  />
                </div>



                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="designer"
                        checked={formData.role === 'designer'}
                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'designer' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Designer</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="client"
                        checked={formData.role === 'client'}
                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'client' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Client</span>
                    </label>
                    {user?.role === 'admin' && (
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="admin"
                          checked={formData.role === 'admin'}
                          onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' }))}
                          className="mr-2"
                        />
                        <span className="text-sm">Admin</span>
                      </label>
                    )}
                  </div>
                </div>

                {formData.role === 'designer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Plan
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="unlimited"
                          checked={formData.subscriptionTier === 'unlimited'}
                          onChange={(e) => setFormData(prev => ({ ...prev, subscriptionTier: e.target.value as 'unlimited' }))}
                          className="mr-2"
                        />
                        <span className="text-sm">Unlimited</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="two_projects"
                          checked={formData.subscriptionTier === 'two_projects'}
                          onChange={(e) => setFormData(prev => ({ ...prev, subscriptionTier: e.target.value as 'two_projects' }))}
                          className="mr-2"
                        />
                        <span className="text-sm">Two Projects</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="one_project"
                          checked={formData.subscriptionTier === 'one_project'}
                          onChange={(e) => setFormData(prev => ({ ...prev, subscriptionTier: e.target.value as 'one_project' }))}
                          className="mr-2"
                        />
                        <span className="text-sm">One Project</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={saving || !formData.name.trim() || !formData.email.trim()}
                >
                  {saving ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementScreen;
